import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Send, ShieldCheck, Sparkles } from "lucide-react";
import { db } from "../lib/db";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import {
  TELEGRAM_BOT_USERNAME,
  ensureTelegramWebAppScript,
  getTelegramLanguageCode,
  isTelegramMiniApp,
  signInWithTelegram,
  TelegramAuthError,
} from "../lib/telegramAuth";
import { detectBrowserLang } from "../i18n";
import { PrivacyPolicyBody } from "./PrivacyPolicyBody";
import { capturePlatformLaunchIntent } from "../lib/launchIntent";
import { normalizeAccountLinkCode } from "../lib/internationalHost";

/** Local one-time flag, stamped only on acceptance — matches
    profiles.consent_accepted_at being stamped server-side exactly once.
    "Not now" is a dismissal for this launch, not a stored refusal: the
    offer comes back on the next launch, same as GW2DO. */
const CONSENT_KV_KEY = "telegram:consentDecision";
const REQUEST_CONSENT_EVENT = "lexibit:request-telegram-consent";

export type TelegramConsentDecision = "accepted" | null;

/** Read-only recovery API for Mini App account UI. */
// eslint-disable-next-line react-refresh/only-export-components
export async function getTelegramConsentDecision(): Promise<TelegramConsentDecision> {
  const row = await db.kv.get(CONSENT_KV_KEY);
  return row?.value === "accepted" ? "accepted" : null;
}

/** Ask the mounted gate to show the consent screen again. Nothing is
 * stored until the user explicitly accepts. */
// eslint-disable-next-line react-refresh/only-export-components
export function requestTelegramConsent(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(REQUEST_CONSENT_EVENT));
}

const TELEGRAM_ENABLED = isSupabaseConfigured && Boolean(TELEGRAM_BOT_USERNAME);

/** Deep-links the bot passed via `startapp=...` are only applied once the
    gate is out of the way (whether that took a decision or none was
    needed) — see docs/DEPLOY.md for the supported `start_param` values. */
function captureStartIntent(): void {
  capturePlatformLaunchIntent();
}

/**
 * Gates the whole app behind a data-processing consent screen the first
 * time it's opened as a Telegram Mini App (launched from the bot). Regular
 * web visits never trigger the check — {@link TELEGRAM_ENABLED} short-
 * circuits before touching Telegram's SDK or Dexie.
 */
export function TelegramConsentGate({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [showMode, setShowMode] = useState<"initial" | "recovery" | null>(null);

  useEffect(() => {
    if (!TELEGRAM_ENABLED) return;
    if (/^\/s\/[A-Za-z0-9_-]{22}$/.test(window.location.pathname)) return;
    // The policy has to be readable before deciding, so the gate stands aside
    // on its route instead of covering it with the consent screen.
    if (window.location.pathname === "/privacy") return;
    let cancelled = false;
    const requestConsent = () => {
      void ensureTelegramWebAppScript().then(() => {
        if (!cancelled && isTelegramMiniApp()) {
          void i18n.changeLanguage(detectBrowserLang(getTelegramLanguageCode())).then(() => {
            if (!cancelled) setShowMode("recovery");
          });
        }
      });
    };
    window.addEventListener(REQUEST_CONSENT_EVENT, requestConsent);
    (async () => {
      await ensureTelegramWebAppScript();
      if (cancelled || !isTelegramMiniApp()) return;
      await i18n.changeLanguage(detectBrowserLang(getTelegramLanguageCode()));
      if (cancelled) return;
      const existing = await getTelegramConsentDecision();
      if (cancelled) return;
      if (!existing) {
        setShowMode("initial");
        return;
      }
      // A device that already consented gets its session back without being
      // asked again: the stored session can be gone (expired refresh token,
      // cleared storage) while the decision survives in Dexie, and that used
      // to surface as a "sign in to sync" card on every launch.
      if (existing === "accepted" && supabase) {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        // Best-effort: a failure just leaves the local-only experience.
        if (!data.session) await signInWithTelegram().catch(() => {});
        if (cancelled) return;
      }
      captureStartIntent();
    })();
    return () => {
      cancelled = true;
      window.removeEventListener(REQUEST_CONSENT_EVENT, requestConsent);
    };
  }, [i18n]);

  if (!showMode) return <>{children}</>;
  return (
    <TelegramConsentScreen
      onDecide={() => {
        const shouldCaptureLaunch = showMode === "initial";
        setShowMode(null);
        if (shouldCaptureLaunch) captureStartIntent();
      }}
    />
  );
}

function TelegramConsentScreen({ onDecide }: { onDecide: () => void }) {
  const { t } = useTranslation();
  // Reading the policy interrupts the decision rather than navigating away:
  // this screen wraps the router, so a link here would reload the whole app
  // and come back to the same question.
  const [readingPolicy, setReadingPolicy] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useCode, setUseCode] = useState(false);
  const [code, setCode] = useState("");
  const normalizedCode = normalizeAccountLinkCode(code);

  // A dismissal, not a stored refusal — see CONSENT_KV_KEY.
  const decline = () => {
    onDecide();
  };

  const accept = async (linkCode?: string) => {
    setError(null);
    setPending(true);
    try {
      await signInWithTelegram({ consent: true, linkCode });
      await db.kv.put({ id: CONSENT_KV_KEY, value: "accepted" });
      onDecide();
    } catch (authError) {
      setError(
        authError instanceof TelegramAuthError
          ? t(`hostAuth.errors.${authError.code}`)
          : t("telegramConsent.error"),
      );
    } finally {
      setPending(false);
    }
  };

  if (readingPolicy) {
    return (
      <main className="fixed inset-x-0 top-0 z-50 h-[var(--host-viewport-stable-height)] overflow-y-auto bg-slate-50 dark:bg-slate-950">
        <div className="mx-auto max-w-2xl p-6" style={{ paddingTop: "max(1.5rem, var(--host-content-safe-area-inset-top))", paddingBottom: "max(1.5rem, var(--host-content-safe-area-inset-bottom))" }}>
          <button
            type="button"
            onClick={() => setReadingPolicy(false)}
            className="mb-6 inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-slate-700 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:text-slate-300 dark:hover:text-slate-100"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common.back")}
          </button>
          <PrivacyPolicyBody />
        </div>
      </main>
    );
  }

  return (
    <main
      className="fixed inset-x-0 top-0 z-50 flex h-[var(--host-viewport-stable-height)] items-center justify-center bg-slate-950/60 p-4"
      style={{
        paddingTop: "max(1rem, var(--host-content-safe-area-inset-top))",
        paddingRight: "max(1rem, var(--host-content-safe-area-inset-right))",
        paddingBottom: "max(1rem, var(--host-content-safe-area-inset-bottom))",
        paddingLeft: "max(1rem, var(--host-content-safe-area-inset-left))",
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-xl">
        <div className="mb-5 text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-indigo-600 text-white">
            <Sparkles className="h-6 w-6" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Lexibit</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t("telegramConsent.tagline")}</p>
          <p className="mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
            {t("telegramConsent.valueLine")}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t("telegramConsent.title")}
            </h2>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400">{t("telegramConsent.body")}</p>
        </div>

        {error && <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        {useCode && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              {t("hostAuth.codeLabel")}
            </label>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              maxLength={11}
              placeholder="ABCD-EFGH"
              className="mt-2 min-h-11 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-center font-mono text-xl font-semibold uppercase tracking-[0.18em] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {t("hostAuth.linkBody")}
            </p>
          </div>
        )}
        <div className="mt-5 flex flex-col gap-2 pb-[env(safe-area-inset-bottom)]">
          <button
            type="button"
            onClick={() => void accept(useCode ? normalizedCode : undefined)}
            disabled={pending || (useCode && normalizedCode.length !== 8)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {pending
              ? t("telegramConsent.signingIn")
              : useCode
                ? t("hostAuth.linkAndContinue")
                : t("telegramConsent.accept")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setUseCode((value) => !value);
            }}
            disabled={pending}
            className="min-h-11 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {useCode ? t("common.back") : t("hostAuth.existingAccount")}
          </button>
          <button
            type="button"
            onClick={decline}
            disabled={pending}
            className="min-h-11 rounded-xl px-5 py-3 text-sm font-medium text-slate-600 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:text-slate-300 dark:hover:text-slate-100 disabled:opacity-50"
          >
            {t("telegramConsent.decline")}
          </button>
          {/* The published policy, not a repeat of the block above it. */}
          <button
            type="button"
            onClick={() => setReadingPolicy(true)}
            className="min-h-11 rounded-xl px-5 text-center text-xs text-slate-600 underline hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:text-slate-300 dark:hover:text-slate-100"
          >
            {t("telegramConsent.policyLink")}
          </button>
        </div>
      </div>
    </main>
  );
}
